'use client';

import { Card, List } from '../components/odds-page-items';
import { OddPageProps as SportOddsTableProps, MatchGroup } from '../types';
import clsx from 'clsx';
import { ReactNode, useEffect, useState } from 'react';
import { getGroupLeagues } from '@/utils/odds-utils';
import { useAtomValue } from 'jotai';
import { oddsPageAtom } from '@/atoms/odds-page';
import { IO } from '@nf/utils-common/fpw/monadic';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { useAppearanceStore } from '@/store/appearance';

export const SportOddsTable = ({ sport, market, matchCollection }: SportOddsTableProps) => {
	const [oddsPageLayout, fontSize] = useAppearanceStore((state) => [state.oddsPageLayout, state.fontSize]);
	const [matchGroups, setMatchGroups] = useState<MatchGroup[]>([]);
	const oddsData = useAtomValue(oddsPageAtom);
	const [expandedMatchCount, setExpandedMatchCount] = useState<number>(10);
	const { SiteConfigsData } = useGetSiteConfigs();
	const { filterSabaString } = useFilterSabaString();

	const isListTheme = oddsPageLayout === 'list';
	const isCardTheme = oddsPageLayout === 'card';

	const containerStyle = clsx(
		{
			'odds-table-list': isListTheme,
			'odds-table-card': isCardTheme,
			[`odds-table-list--sport${sport}`]: isListTheme,
			'odds-table-list--early': market === 'e',
		},
		`fontsize-${fontSize}`
	);
	let accumulatedGroupMatches = 0;
	const convertExpandedMatchCount = (sportId: number, expandedMatchCount?: string): number => {
		const stringsMap = new Map();
		let result = 10;
		if (expandedMatchCount !== '') {
			const values = expandedMatchCount?.split(';');
			values?.forEach(function (value) {
				const parts = value.split('_');
				if (parts.length === 2) {
					stringsMap.set(parts[0], parts[1]);
				}
			});
		}
		if (stringsMap.has(sportId.toString())) {
			result = Number(stringsMap.get(sportId.toString()));
		} else {
			result = Number(stringsMap.get('def'));
		}
		return result;
	}
	const filterSabaStringToLeague = (matchGroup: MatchGroup[]) => {
		matchGroup.forEach((match) => {
			match.LeagueName = filterSabaString(match.LeagueName, match.LeagueGroupId);
		});
		return matchGroup;
	}

	useEffect(() => {
		setExpandedMatchCount(convertExpandedMatchCount(sport, SiteConfigsData?.ExpandedMatchCount))
	}, [SiteConfigsData?.ExpandedMatchCount])

	useEffect(() => {
		IO.of(matchCollection)
			.map(getGroupLeagues(oddsData?.leagueNameMap))
			.map(filterSabaStringToLeague)
			.map(setMatchGroups)
			.run()
	}, [matchCollection]);

	return (
		<div className={containerStyle}>
			{
				matchGroups?.reduce((nodes: ReactNode[], matchGroup: MatchGroup): ReactNode[] => {
					accumulatedGroupMatches += matchGroup.Matches.length;
					if (isListTheme) {
						nodes.push(
							<List
								key={`${matchGroup.LeagueId}_${nodes.length}`}
								LeagueName={matchGroup.LeagueName}
								Matches={matchGroup.Matches}
								sportId={sport}
								isExpand={accumulatedGroupMatches <= expandedMatchCount || nodes.length === 0}
								leagueIdForMyFavourite={matchGroup.LeagueId}
								LeagueGroupId={matchGroup.LeagueGroupId}
							/>
						);
					} else if (isCardTheme) {
						nodes.push(
							<Card
								key={`${matchGroup.LeagueId}_${nodes.length}`}
								LeagueName={matchGroup.LeagueName}
								Matches={matchGroup.Matches}
								sportId={sport}
								leagueIdForMyFavourite={matchGroup.LeagueId}
								LeagueGroupId={matchGroup.LeagueGroupId}
								isExpand={accumulatedGroupMatches <= expandedMatchCount || nodes.length === 0}
							/>
						)
					}
					return nodes;
				}, [])
			}
		</div>
	);
};